import { Link, useLocation } from 'react-router-dom';
import { ADRESSE_COURTE, ENTREPRISE } from '../data/entreprise';
import { GererCookies } from './CookieConsent';
import { LIBELLE_LIFT, sectionDePage, type Section } from '../data/navigation';
import { colonneContextuelle } from '../data/footer-contextuel';
import { liensDuCorps } from '../data/liens-corps';
import type { CommuneSEO } from '../data/communes';
import { estPubliable, PAGE_PRIX_DEMENAGEMENT } from '../data/pages-en-preparation';

const FACEBOOK_URL = 'https://www.facebook.com/GrammeDemenagements';

export interface FooterProps {
  /**
   * Section que le GABARIT déclare pour cette page — « demenagement » pour une
   * page commune, « adresse » pour une fiche opérateur. Absente : « generique ».
   * Voir src/data/navigation.ts : c'est une déclaration, pas une détection.
   */
  section?: Section;
  /**
   * Slug CMS de la page, quand elle a une ligne dans la table `pages`. La
   * colonne `pages.section` l'emporte alors sur la déclaration du gabarit.
   */
  slug?: string;
  /**
   * La commune de la page, quand c'en est une : la colonne géographique rend
   * alors sa deuxième couronne plutôt que les communes du menu, identiques
   * partout. Déclarée par le gabarit, jamais déduite de l'URL.
   */
  commune?: CommuneSEO;
}

const lien = 'inline-block py-1.5 hover:text-white transition-colors';

/**
 * Pied de page — trois colonnes fixes et une colonne contextuelle (lot 16).
 *
 * Fixes, identiques sur toutes les pages : la marque (1), « Nos services » (2),
 * « Informations & contact » (4) et la ligne légale. Contextuelle (3) : son
 * contenu suit la section de la page, résolu au pré-rendu par
 * `colonneContextuelle` — jamais plus de neuf liens, jamais la page courante,
 * jamais un lien que le corps porte déjà, jamais vide.
 *
 * Le bloc NAP — adresse, téléphone, horaires, TVA — est repris tel quel : il
 * est cohérent avec le JSON-LD `MovingCompany`, et le contrôle de build les
 * compare.
 *
 * Les attributs `data-*` sont lus par scripts/verify-build.mjs (bloc
 * « Lot 16 ») : la section rendue, la colonne effectivement servie après
 * repli, et les colonnes fixes à comparer entre pages témoins.
 */
export default function Footer({ section, slug, commune }: FooterProps) {
  const { pathname } = useLocation();
  const sectionPage = sectionDePage(slug, section);
  // Les liens que le corps porte déjà sont collectés au build depuis le HTML
  // rendu (src/data/liens-corps.json), plus déclarés par chaque gabarit.
  const contextuelle = colonneContextuelle(sectionPage, pathname, liensDuCorps(pathname), { commune });
  const pagePrix = estPubliable(PAGE_PRIX_DEMENAGEMENT);

  return (
    <footer
      id="pied-de-page"
      data-section={sectionPage}
      data-colonne-contextuelle={contextuelle.section}
      data-repli={contextuelle.repli}
      className="bg-footer-bg text-white py-16 px-4 md:px-8"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
        <div data-colonne="marque">
          <div className="mb-4 inline-block">
            <div className="bg-white rounded-xl px-3 py-1.5">
              <picture>
                <source srcSet="/logo-gramme-300.webp" type="image/webp" />
                <img
                  src="/logo-gramme-300.png"
                  alt="Déménagements Gramme"
                  className="h-10 w-auto"
                  loading="lazy"
                  width="300"
                  height="224"
                />
              </picture>
            </div>
          </div>
          <p className="text-white/60 text-sm mb-4">
            Votre déménageur de confiance à Liège depuis 1948.
          </p>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-9 h-9 rounded-full bg-yellow items-center justify-center hover:bg-white transition-colors"
            aria-label="Facebook"
          >
            <svg className="w-4 h-4 text-navy" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          </a>
        </div>

        <div data-colonne="services">
          <h3 className="text-yellow text-[11px] uppercase tracking-[0.2em] font-bold mb-4">
            NOS SERVICES
          </h3>
          <ul className="space-y-2 text-white/60 text-sm">
            <li><Link to="/demenagement" className={lien}>Déménagement</Link></li>
            {/* Les pages qui se positionnent, pas seulement les hubs : c'est
                ici que « Déménagement à Liège » et « Garde-meubles à Liège »
                reçoivent leur lien sitewide. */}
            <li><Link to="/demenagement/demenagement-liege" className={lien}>Déménagement à Liège</Link></li>
            <li><Link to="/demenagement/demenagement-entreprise" className={lien}>Déménagement d'entreprise</Link></li>
            <li><Link to="/garde-meubles/garde-meubles-liege" className={lien}>Garde-meubles à Liège</Link></li>
            <li><Link to="/monte-meubles" className={lien}>{LIBELLE_LIFT}</Link></li>
            <li><Link to="/demenagement/demenagement-international" className={lien}>Déménagement international</Link></li>
            <li><Link to="/estimation-volume" className={lien}>Estimer mon volume</Link></li>
          </ul>
        </div>

        <div data-colonne="contextuelle">
          {/* Un groupe d'ordinaire ; deux quand la section n'a pas encore assez
              de pages publiées et que la colonne complète avec les communes. */}
          {contextuelle.groupes.map((g, i) => (
            <div key={g.titre} className={i > 0 ? 'mt-6' : undefined}>
              <h3 className="text-yellow text-[11px] uppercase tracking-[0.2em] font-bold mb-4">
                {g.titre}
              </h3>
              <ul className="space-y-2 text-white/60 text-sm">
                {g.liens.map((l) => (
                  <li key={l.to}><Link to={l.to} className={lien}>{l.label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div data-colonne="informations">
          <h3 className="text-yellow text-[11px] uppercase tracking-[0.2em] font-bold mb-4">
            INFORMATIONS &amp; CONTACT
          </h3>
          <ul className="space-y-2 text-white/60 text-sm">
            {/* Résolu par le verrou de publication : pas de lien mort en
                attendant le lot 6. */}
            {pagePrix && (
              <li><Link to={PAGE_PRIX_DEMENAGEMENT} className={lien}>Prix d'un déménagement</Link></li>
            )}
            <li><Link to="/blog" className={lien}>Blog</Link></li>
            <li>{ADRESSE_COURTE}</li>
            <li>
              Tél : <a href="tel:+3242645016" className={lien}>04 264 50 16</a>
            </li>
            <li>
              <a href="mailto:contact@demenagements-gramme.be" className={`${lien} break-all`}>
                contact@demenagements-gramme.be
              </a>
            </li>
            <li className="pt-1">{ENTREPRISE.horaires.affichage}</li>
            <li>Dim : Fermé</li>
            <li className="text-white/60 text-xs">TVA BE 0775.264.382</li>
          </ul>
          <Link
            to="/contact-devis"
            className="mt-4 w-full bg-yellow text-navy font-bold uppercase py-2.5 rounded text-sm hover:bg-white transition-colors block text-center"
          >
            Devis Gratuit
          </Link>
        </div>
      </div>

      <div data-colonne="legale" className="max-w-7xl mx-auto mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="text-white/60 text-[13px]">
          &copy; 2026 Déménagements Gramme. Tous droits réservés.
        </p>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-white/60 text-[13px]">
          <Link to="/mentions-legales" className={lien}>Mentions légales</Link>
          <span>|</span>
          <Link to="/politique-confidentialite" className={lien}>Confidentialité</Link>
          <span aria-hidden="true">·</span>
          {/* Retirer son consentement doit être aussi simple que le donner. */}
          <GererCookies className={`${lien} underline-offset-2 hover:underline`} />
          <span>|</span>
          <Link to="/conditions-generales" className={lien}>CGV</Link>
          <span>|</span>
          <Link to="/protection-donnees" className={lien}>Protection des données</Link>
        </div>
      </div>
    </footer>
  );
}
